import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMinor } from '../api/payments';
import type { PlayCurrency } from '../api/preferences';
import { aznMinorToPlayMinor, wageringPercent } from '../lib/fxDisplay';
import { fetchWheelStatus } from './lucky-wheel/luckyWheelApi';
import type { WheelGrantStatus, WheelWageringStatus } from './lucky-wheel/wheelTypes';

interface Props {
  open: boolean;
  onClose: () => void;
  playCurrency: PlayCurrency;
  rateAznPerUnit: string;
  locale: string;
}

function statusLabel(status: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const key = `payments:bonusStatus_${status}`;
  const mapped = t(key, { defaultValue: '' });
  if (mapped) return mapped;
  return t('payments:bonusStatus_UNKNOWN', { defaultValue: status || '—' });
}

function GrantCard({
  grant,
  play,
  rate,
  locale,
  t,
}: {
  grant: WheelGrantStatus;
  play: PlayCurrency;
  rate: string;
  locale: string;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const toPlay = (azn: number) => aznMinorToPlayMinor(azn, play === 'AZN' ? 1 : rate);
  const pct = wageringPercent(grant.wagering_completed_minor, grant.wagering_target_minor);
  const name =
    grant.name ||
    t('payments:bonusGrantName', {
      defaultValue: 'Lucky Wheel bonus',
      multiplier: grant.wagering_multiplier || 30,
    });

  return (
    <article className="bonus-panel-grant">
      <header className="bonus-panel-grant-head">
        <div>
          <strong>{name}</strong>
          <span className="muted">
            {t('payments:bonusTypeWheel', { defaultValue: 'Lucky Wheel' })} · {grant.wagering_multiplier || 30}x
          </span>
        </div>
        <span className={`bonus-panel-status status-${(grant.status || 'none').toLowerCase()}`}>
          {statusLabel(grant.status || 'NONE', t)}
        </span>
      </header>

      <dl className="bonus-panel-dl">
        <div>
          <dt>{t('payments:bonusAwarded', { defaultValue: 'Verilmiş bonus' })}</dt>
          <dd>{formatMinor(toPlay(grant.awarded_minor), play, locale)}</dd>
        </div>
        <div>
          <dt>{t('payments:bonusBalanceNow', { defaultValue: 'Hazırkı bonus balansı' })}</dt>
          <dd>{formatMinor(toPlay(grant.grant_balance_minor ?? grant.awarded_minor), play, locale)}</dd>
        </div>
        <div>
          <dt>{t('payments:bonusWagerTarget', { defaultValue: 'Tələb olunan dövriyyə' })}</dt>
          <dd>{formatMinor(toPlay(grant.wagering_target_minor), play, locale)}</dd>
        </div>
        <div>
          <dt>{t('payments:bonusWagerDone', { defaultValue: 'Tamamlanmış dövriyyə' })}</dt>
          <dd>{formatMinor(toPlay(grant.wagering_completed_minor), play, locale)}</dd>
        </div>
        <div>
          <dt>{t('payments:bonusWagerLeft', { defaultValue: 'Qalan dövriyyə' })}</dt>
          <dd>{formatMinor(toPlay(grant.wagering_remaining_minor), play, locale)}</dd>
        </div>
        {grant.expires_at ? (
          <div>
            <dt>{t('payments:bonusExpires', { defaultValue: 'Bitmə vaxtı' })}</dt>
            <dd>{new Date(grant.expires_at).toLocaleString(locale)}</dd>
          </div>
        ) : null}
      </dl>

      <div className="bonus-panel-progress" aria-label={`${pct}%`}>
        <div className="bonus-panel-progress-track">
          <div className="bonus-panel-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span>{t('payments:bonusCompletion', { defaultValue: 'Tamamlanma: {{pct}}%', pct })}</span>
      </div>
    </article>
  );
}

export function BonusPanel({ open, onClose, playCurrency, rateAznPerUnit, locale }: Props) {
  const { t } = useTranslation(['payments', 'common']);
  const [wagering, setWagering] = useState<WheelWageringStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchWheelStatus()
      .then((status) => {
        if (cancelled) return;
        setWagering(status.wagering || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const grants = useMemo(() => {
    const list = wagering?.grants || [];
    return list.filter((g) => g && typeof g.id === 'number');
  }, [wagering]);

  if (!open) return null;

  return (
    <div className="bonus-panel-backdrop" role="presentation" onClick={onClose}>
      <div
        className="bonus-panel-sheet"
        role="dialog"
        aria-label={t('payments:bonusDetailsTitle', { defaultValue: 'Bonus məlumatları' })}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="bonus-panel-head">
          <strong>{t('payments:bonusDetailsTitle', { defaultValue: 'Bonus məlumatları' })}</strong>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common:close')}>
            ×
          </button>
        </header>

        <p className="bonus-panel-currency muted">
          {t('payments:displayCurrency', { defaultValue: 'Göstərilən valyuta' })}: {playCurrency}
        </p>

        {loading ? <p className="muted">{t('common:loading', { defaultValue: 'Yüklənir…' })}</p> : null}
        {error ? <p className="form-error">{error}</p> : null}

        {!loading && !error && grants.length === 0 ? (
          <p className="bonus-panel-empty">
            {t('payments:bonusEmpty', {
              defaultValue: 'Aktiv bonus yoxdur. Lucky Wheel və ya kampaniya bonusu qazandıqda burada görünəcək.',
            })}
          </p>
        ) : null}

        <div className="bonus-panel-list">
          {grants.map((grant) => (
            <GrantCard
              key={grant.id}
              grant={grant}
              play={playCurrency}
              rate={rateAznPerUnit}
              locale={locale}
              t={t}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
